import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AlertCircle, CalendarDays, Clock, Users, Utensils, ArrowRight,
  CheckCircle2, XCircle, Loader2, MapPin, Sparkles, LayoutGrid, UtensilsCrossed, UserRound,
} from 'lucide-react';
import { customerAuth } from '../../services/customerService';
import { confirm, alertDialog } from '../../components/ui/confirm-dialog';
import { useT } from '../../context/LanguageContext';

/**
 * CustomerReserve — public, mobile-only table reservation page.
 *   /customer/reserve?restaurant=<id>[&location=<id>]
 * Flow: pick date · time · party size → either "best available table"
 * (default, auto-assigned server-side) or pick a preferred section — the
 * floor map is never shown to customers.
 */
const API_URL = process.env.REACT_APP_API_URL || '/api';
// Warm maroon/cream scheme (matches the customer menu look):
const ORANGE = '#8C3A23';      // brand maroon — selected chips, CTA, accents
const ACCENT = '#EA6A12';      // orange tile + icon accents (CustomerMenu's ORANGE)
const MAROON = '#3E1608';      // the dark header shell, same as CustomerMenu
const TINT = '#FBEEE4';        // selected-light backgrounds

const customerHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Customer ${customerAuth.token()}`,
});

const pad2 = (n) => String(n).padStart(2, '0');
const toDateStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Time picker: hours & 15-minute slots generate from the restaurant's opening
// hours. Times are tracked as minutes-since-midnight of the chosen date; a
// closing time at or before opening (e.g. 9 AM – 1 AM) wraps past midnight,
// so a slot may land on the next calendar day.
const DEFAULT_OPEN = '09:00';
const DEFAULT_CLOSE = '23:00';
const parseHM = (t) => {
  const [h, m] = String(t).split(':').map(Number);
  return h * 60 + m;
};
const fmt12 = (mins) => {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${h < 12 ? 'AM' : 'PM'}`;
};
// Preference accordions, Tock-style: collapsed "+" rows the guest opens only
// if they have something to say. Each maps onto the same requestOpts/notes
// that already go into the reservation note.
const OCCASION_OPTS = ['Birthday / surprise cake', 'Anniversary'];
const ACCESS_OPTS = ['High chair', 'Wheelchair access', 'Window seat', 'Quiet corner'];

// How a refunded booking amount reaches the guest, in the guest's own terms.
// The restaurant picks one in Settings -> Reservations (REFUND_METHODS on
// ReservationSettingsPage); these are the same four said the other way round,
// so keep the two wordings in step. Unknown value falls back to "same source",
// which is what every booking taken before this setting existed got.
const REFUND_METHOD_TEXT = {
  SOURCE: 'back to the payment method you booked with',
  CASH: 'as cash, to collect at the restaurant',
  CREDIT: 'as house credit towards a future visit',
  BANK: 'by bank transfer to your account',
};
const refundHow = (m) => REFUND_METHOD_TEXT[m] || REFUND_METHOD_TEXT.SOURCE;

// Defined at module level: a component declared inside the page would remount
// on every re-render and lose focus while typing (see CustomerMenu).
function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-gray-900 mb-3">{title}</h2>
      {children}
    </div>
  );
}

function Accordion({ title, open, onToggle, children }) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button type="button" onClick={onToggle}
        className="w-full flex items-center gap-3 py-3.5 text-left text-sm text-gray-800">
        <span className="text-gray-400 text-base leading-none w-3">{open ? '–' : '+'}</span>
        {title}
      </button>
      {open && <div className="pb-4 pl-6">{children}</div>}
    </div>
  );
}

export default function CustomerReserve() {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const restaurantId = params.get('restaurant') || '';
  const presetLocationId = params.get('location') || '';

  const isCustomerAuthed = customerAuth.isLoggedIn();
  // Who is booking — shown in the header band so the guest always knows which
  // account the reservation lands on.
  const profile = customerAuth.profile() || {};
  const customerDisplayName =
    [profile.salutation, [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.name || profile.full_name]
      .filter(Boolean).join(' ');
  // Reserve-first flow: any guest can fill the whole booking form without an
  // account. Sign-in is only required at the final confirmation step, so an
  // un-authenticated guest is always in "browse" mode until they sign in.
  const browseOnly = !isCustomerAuthed;

  // Persisted so the guest's selections survive the sign-in round-trip and come
  // back ready to confirm (see the restore effect below).
  const PENDING_KEY = `pendingReserve:${restaurantId}`;

  // Send the guest to sign in, saving their in-progress booking first so it is
  // restored (not retyped) when they land back here authenticated.
  const promptLogin = () => {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({
        locationId, date, timeMins, hourStr, minStr, meridiem, pax,
        seatMode, floorId, sectionId, notes, requestOpts, diningType, buffetId,
        dietary, smsOptIn,
      }));
    } catch { /* storage full / disabled — just proceed to login */ }
    const p = new URLSearchParams(location.search);
    p.delete('browse');
    const q = p.toString();
    const nextUrl = encodeURIComponent(location.pathname + (q ? `?${q}` : ''));
    navigate(`/customer/account-login?next=${nextUrl}`);
  };

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(presetLocationId);
  const [floors, setFloors] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);

  const [date, setDate] = useState(() => toDateStr(new Date()));
  // Selected time as minutes past midnight of `date` (may exceed 1440 when the
  // restaurant closes after midnight); null = not chosen yet.
  const [timeMins, setTimeMins] = useState(null);
  // Typed time — hour (1-12), minute (0-59) and AM/PM.
  const [hourStr, setHourStr] = useState('');
  const [minStr, setMinStr] = useState('');
  const [meridiem, setMeridiem] = useState('PM');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pax, setPax] = useState(2);
  const [seatMode, setSeatMode] = useState('best'); // 'best' | 'section'
  const [floorId, setFloorId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [notes, setNotes] = useState('');
  const [requestOpts, setRequestOpts] = useState([]);

  const toggleRequestOpt = (opt) =>
    setRequestOpts((prev) =>
      prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]);

  // Which preference accordion is open ('occasion' | 'dietary' | 'seating' |
  // 'other' | null) — one at a time keeps the page short.
  const [openPanel, setOpenPanel] = useState(null);
  const togglePanel = (key) => setOpenPanel((cur) => (cur === key ? null : key));
  // Free-text dietary note, kept apart from the general note so both read
  // clearly in the reservation the restaurant sees.
  const [dietary, setDietary] = useState('');
  // Consent boxes — the cancellation policy is required before submitting.
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(true);

  // Dining style: regular menu ordering or an open buffet (per-head).
  const [diningType, setDiningType] = useState('ALACARTE'); // 'ALACARTE' | 'BUFFET'
  const [buffets, setBuffets] = useState([]);
  const [buffetsLoading, setBuffetsLoading] = useState(false);
  const [buffetId, setBuffetId] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [booked, setBooked] = useState(null);
  const [myReservations, setMyReservations] = useState([]);

  // Reservation policy (booking amount, refunds, hold times, pre-order) —
  // public read, drives the mock payment step and the policy text.
  const [policy, setPolicy] = useState(null);
  const maxParty = Number(policy?.max_party_size) || 50;
  /* Whether THIS booking may pre-order. The policy is fetched once for the
     restaurant, but a buffet booking never pre-orders whatever it says: a
     buffet is one price per head and one counter everybody helps themselves
     from, so there are no dishes to choose. The server refuses it too — this
     is only so the button is never dangled in front of a guest who would then
     be told no. */
  const canPreorder = (r) => !!policy?.allow_preorder && r?.dining_type !== 'BUFFET';
  /* The policy THIS booking answers to. The endpoint hands back the whole
     settings row; a BUFFET booking follows the buffet_* half of it — its own
     amount, its own refund window — whichever screen it was started from,
     which is exactly the rule the server applies when it takes the booking.
     Reading the website's half for a buffet would quote the guest a number
     they will never be charged. */
  const bookingPolicy = useMemo(() => {
    if (!policy) return null;
    if (diningType !== 'BUFFET') return { ...policy, amount_mode: 'FIXED' };
    return {
      ...policy,
      payment_required: policy.buffet_payment_required,
      booking_amount: policy.buffet_booking_amount,
      amount_per: policy.buffet_amount_per,
      /* FIXED = a deposit off the bill; FULL = the whole per-head ticket,
         nothing left to pay at the table. */
      amount_mode: policy.buffet_amount_mode || 'FIXED',
      refund_allowed: policy.buffet_refund_allowed,
      refund_cutoff_minutes: policy.buffet_refund_cutoff_minutes,
      hold_table_minutes: policy.buffet_hold_table_minutes,
    };
  }, [policy, diningType]);
  // Booking awaiting its (mock) payment — shows the payment screen.
  const [paying, setPaying] = useState(null);
  /* The seat hold, counted down on screen. A buffet booking takes its covers
     out of the day's capacity the moment it is made and keeps them only while
     this runs — so the guest is shown the clock they are actually racing
     rather than finding out afterwards that it ran out. Null = this booking
     is on no clock at all. */
  const [holdLeft, setHoldLeft] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const [cardNo, setCardNo] = useState('');
  const [cardExp, setCardExp] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [preorderBusy, setPreorderBusy] = useState(false);

  const restaurantMissing = !restaurantId;
  // Master switch off → the booking form is replaced by a "closed" notice
  // (in-flight payment / confirmation screens still finish normally).
  const reservationsClosed = policy?.online_reservations_enabled === false;

  // Branches.
  useEffect(() => {
    if (!restaurantId || (!isCustomerAuthed && !browseOnly)) return;
    fetch(`${API_URL}/customers/restaurants/${restaurantId}/locations/`)
      .then((r) => r.json())
      .then((d) => {
        const rows = (d && d.success && d.data) || [];
        setLocations(rows);
        if (!presetLocationId && rows.length === 1) setLocationId(String(rows[0].id));
      })
      .catch(() => setLocations([]));
  }, [restaurantId, isCustomerAuthed, browseOnly, presetLocationId]);

  // Floor plan (floors → sections → tables) for the chosen branch.
  useEffect(() => {
    if (!locationId) { setFloors([]); return; }
    setTablesLoading(true);
    setSeatMode('best'); setFloorId(''); setSectionId('');
    fetch(`${API_URL}/customers/locations/${locationId}/tables/`)
      .then((r) => r.json())
      .then((d) => {
        const fl = (d && d.floors) || [];
        setFloors(fl);
        // Default to the first floor + first section with tables.
        const firstFloor = fl[0];
        if (firstFloor) {
          setFloorId(String(firstFloor.id));
          const firstSec = (firstFloor.sections || [])[0];
          if (firstSec) setSectionId(String(firstSec.id));
        }
      })
      .catch(() => setFloors([]))
      .finally(() => setTablesLoading(false));
  }, [locationId]);

  // Buffets on offer at the chosen branch, priced at the planned visit time
  // (so weekend/festive rates show the amount the guest will actually pay).
  useEffect(() => {
    if (!locationId) { setBuffets([]); setBuffetId(''); return; }
    setBuffetsLoading(true);
    // Price buffets at the planned visit moment (after-midnight slots roll over).
    const visit = timeMins != null
      ? new Date(new Date(`${date}T00:00:00`).getTime() + timeMins * 60000)
      : null;
    const at = visit
      ? `?at=${encodeURIComponent(`${toDateStr(visit)}T${pad2(visit.getHours())}:${pad2(visit.getMinutes())}`)}`
      : '';
    fetch(`${API_URL}/customers/locations/${locationId}/buffets/${at}`)
      .then((r) => r.json())
      .then((d) => {
        const rows = (d && d.success && d.data) || [];
        setBuffets(rows);
        setBuffetId((cur) => (rows.some((b) => String(b.id) === String(cur)) ? cur : ''));
      })
      .catch(() => setBuffets([]))
      .finally(() => setBuffetsLoading(false));
  }, [locationId, date, timeMins]);

  /* The buffet being booked, and the sittings it offers on the chosen date.
     A buffet split into sittings is booked AT one of them — the guest picks
     "10:15 – 12:15" and that IS the reservation time, so the picker writes
     the typed time fields the rest of the page already reads. */
  const chosenBuffet = useMemo(
    () => buffets.find((b) => String(b.id) === String(buffetId)) || null,
    [buffets, buffetId],
  );
  const buffetSittings = (chosenBuffet && chosenBuffet.sittings) || [];
  const pickSitting = (start) => {
    const mins = parseHM(start);
    const hh = Math.floor(mins / 60) % 24;
    setHourStr(String(hh % 12 === 0 ? 12 : hh % 12));
    setMinStr(pad2(mins % 60));
    setMeridiem(hh < 12 ? 'AM' : 'PM');
  };

  // Booking policy of the BRANCH being booked (public; rules are per branch).
  useEffect(() => {
    if (!restaurantId) return;
    const q = locationId ? `?location=${locationId}` : '';
    fetch(`${API_URL}/customers/restaurants/${restaurantId}/reservation-policy/${q}`)
      .then((r) => r.json())
      .then((d) => setPolicy((d && d.success && d.data) || null))
      .catch(() => setPolicy(null));
  }, [restaurantId, locationId]);

  // Live availability — only when the restaurant confirms bookings instantly
  // (auto_confirm): tells the guest whether a table is actually free at that
  // time (in their chosen section) before they submit. With staff approval,
  // the request is reviewed anyway, so no pre-check is shown.
  const [availability, setAvailability] = useState(null); // null | 'checking' | {available}
  useEffect(() => {
    if (!policy?.auto_confirm || !locationId || timeMins == null || !pax) {
      setAvailability(null);
      return undefined;
    }
    const start = new Date(new Date(`${date}T00:00:00`).getTime() + timeMins * 60000);
    if (Number.isNaN(start.getTime()) || start <= new Date()) {
      setAvailability(null);
      return undefined;
    }
    setAvailability('checking');
    const secId = seatMode === 'section' && sectionId ? Number(sectionId) : '';
    const t = setTimeout(() => {
      const params = new URLSearchParams({
        reserved_start_at: start.toISOString(),
        pax: String(Number(pax) || 1),
      });
      if (secId) params.set('section_id', String(secId));
      fetch(`${API_URL}/customers/locations/${locationId}/reservation-availability/?${params}`)
        .then((r) => r.json())
        .then((d) => setAvailability((d && d.success && d.data) || null))
        .catch(() => setAvailability(null));
    }, 400);
    return () => clearTimeout(t);
  }, [policy, locationId, date, timeMins, pax, seatMode, sectionId]);

  const loadMyReservations = () => {
    fetch(`${API_URL}/customers/reservations/list/`, { headers: customerHeaders() })
      .then((r) => r.json())
      .then((d) => setMyReservations((d && d.success && d.data) || []))
      .catch(() => setMyReservations([]));
  };
  useEffect(() => {
    if (isCustomerAuthed) loadMyReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomerAuthed]);

  // Just signed in and came back — restore the booking the guest was building
  // so they land straight on a ready-to-confirm form instead of an empty one.
  useEffect(() => {
    if (!isCustomerAuthed) return;
    let saved;
    try { saved = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null'); }
    catch { saved = null; }
    if (!saved) return;
    sessionStorage.removeItem(PENDING_KEY);
    if (saved.locationId) setLocationId(saved.locationId);
    if (saved.date) setDate(saved.date);
    if (saved.timeMins != null) setTimeMins(saved.timeMins);
    if (saved.hourStr != null) setHourStr(saved.hourStr);
    if (saved.minStr != null) setMinStr(saved.minStr);
    if (saved.meridiem) setMeridiem(saved.meridiem);
    if (saved.pax != null) setPax(saved.pax);
    if (saved.seatMode) setSeatMode(saved.seatMode);
    if (saved.floorId) setFloorId(saved.floorId);
    if (saved.sectionId) setSectionId(saved.sectionId);
    if (saved.notes) setNotes(saved.notes);
    if (Array.isArray(saved.requestOpts)) setRequestOpts(saved.requestOpts);
    if (saved.diningType) setDiningType(saved.diningType);
    if (saved.buffetId) setBuffetId(saved.buffetId);
    if (saved.dietary) setDietary(saved.dietary);
    if (typeof saved.smsOptIn === 'boolean') setSmsOptIn(saved.smsOptIn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomerAuthed]);

  const activeFloor = floors.find((f) => String(f.id) === String(floorId)) || null;
  const activeSection =
    (activeFloor?.sections || []).find((s) => String(s.id) === String(sectionId)) || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (reservationsClosed) {
      setError('Online reservations are currently closed. Please call the restaurant to book a table.');
      return;
    }
    if (!locationId) { setError('Please choose a branch.'); return; }
    if (!date || timeMins == null) { setError('Please pick a date and time.'); return; }
    if (!startAt || Number.isNaN(startAt.getTime()) || startAt <= new Date()) {
      setError('Reservation time must be in the future.');
      return;
    }
    if (diningType === 'BUFFET' && !buffetId) {
      setError('Please choose which buffet you\'d like to book.');
      return;
    }
    if (!policyAccepted) {
      setError('Please accept the cancellation policy to continue.');
      return;
    }
    // Browsing without an account: booking needs one — sign in and come back.
    if (!customerAuth.isLoggedIn()) {
      promptLogin();
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/customers/reservations/`, {
        method: 'POST',
        headers: customerHeaders(),
        body: JSON.stringify({
          location_id: Number(locationId),
          reserved_start_at: startAt.toISOString(),
          pax: Number(pax) || 1,
          section_id: seatMode === 'section' && sectionId ? Number(sectionId) : undefined,
          dining_type: diningType,
          buffet_id: diningType === 'BUFFET' ? Number(buffetId) : undefined,
          notes: [
            requestOpts.join(', '),
            dietary.trim() && `Dietary: ${dietary.trim()}`,
            notes.trim(),
            smsOptIn ? '' : 'No SMS updates',
          ].filter(Boolean).join(' — '),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Could not book your table. Please try again.');
        return;
      }
      // Booking amount due → mock payment screen first; else straight to done.
      if (data.data?.payment_status === 'PENDING') {
        setPaying(data.data);
        setCardNo(''); setCardExp(''); setCardCvv('');
      } else {
        setBooked(data.data);
      }
      loadMyReservations();
    } catch (err) {
      console.error(err);
      setError('Network error. Please check your connection.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const until = paying?.payment_hold_expires_at;
    if (!until) { setHoldLeft(null); return undefined; }
    const tick = () => setHoldLeft(
      Math.max(0, Math.round((new Date(until).getTime() - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [paying]);
  const holdExpired = holdLeft === 0;
  const mmss = (secs) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  // Mock payment: card fields are cosmetic — the backend just flips PENDING → PAID.
  const payNow = async () => {
    if (!paying) return;
    if (cardNo.replace(/\s/g, '').length < 12 || !cardExp || cardCvv.length < 3) {
      setError('Enter the card number, expiry and CVV to pay.');
      return;
    }
    setPayBusy(true);
    setError('');
    try {
      await new Promise((r) => setTimeout(r, 900)); // gateway round-trip (mock)
      const res = await fetch(`${API_URL}/customers/reservations/${paying.id}/pay/`, {
        method: 'POST', headers: customerHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Payment failed. Please try again.');
        return;
      }
      setBooked(data.data);
      setPaying(null);
      loadMyReservations();
    } catch (err) {
      console.error(err);
      setError('Network error during payment.');
    } finally {
      setPayBusy(false);
    }
  };

  // Abandon an unpaid booking from the payment screen.
  const abandonPayment = async () => {
    if (!paying) return;
    try {
      await fetch(`${API_URL}/customers/reservations/${paying.id}/cancel/`, {
        method: 'POST', headers: customerHeaders(),
      });
    } catch (err) { console.error(err); }
    setPaying(null);
    setError('');
    loadMyReservations();
  };

  // Pre-order: send the guest to the customer menu in pre-order mode; the
  // cart is saved onto this reservation and fired at check-in.
  const startPreorder = async (reservation) => {
    setPreorderBusy(true);
    try {
      const res = await fetch(`${API_URL}/customers/restaurants/${restaurantId}/menus/`);
      const data = await res.json();
      const menu = ((data && data.success && data.data) || [])[0];
      if (!menu) {
        setError('The menu is not available for pre-ordering right now.');
        return;
      }
      navigate(`/customer/menu?restaurant=${restaurantId}&menu=${menu.id}&preorder=${reservation.id}`);
    } catch (err) {
      console.error(err);
      setError('Could not open the menu. Please try again.');
    } finally {
      setPreorderBusy(false);
    }
  };

  const cancelReservation = async (id) => {
    const r = myReservations.find((x) => x.id === id);
    // Spell out what happens to a paid booking amount before cancelling.
    let description = 'The table will be released for other guests.';
    if (r?.payment_status === 'PAID' && policy) {
      const start = r.reserved_start_at ? new Date(r.reserved_start_at) : null;
      const inWindow = policy.refund_allowed && start
        && Date.now() <= start.getTime() - (policy.refund_cutoff_minutes || 0) * 60000;
      description += inWindow
        ? ` Your booking amount of Rs ${Number(r.payment_amount || 0).toFixed(0)} will be refunded ${refundHow(policy.refund_method)}.`
        : ` Your booking amount of Rs ${Number(r.payment_amount || 0).toFixed(0)} will NOT be refunded.`;
    }
    const ok = await confirm({
      title: 'Cancel this reservation?',
      description,
      confirmText: 'Cancel reservation', cancelText: 'Keep it', tone: 'destructive',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/customers/reservations/${id}/cancel/`, {
        method: 'POST', headers: customerHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        loadMyReservations();
        if (r?.payment_status === 'PAID' && data.message) {
          await alertDialog({ title: 'Reservation cancelled', description: data.message });
        }
      }
    } catch (err) { console.error(err); }
  };

  // A booking counts as "upcoming" only while its time is still ahead (a 2h
  // grace matches the staff Reservations list, so both sides show the same
  // set). A pending/confirmed booking whose slot has passed is stale — it
  // drops into history instead of lingering as "Awaiting approval".
  const UPCOMING_GRACE_MS = 2 * 60 * 60 * 1000;
  const isFuture = (r) => {
    const t = r.reserved_start_at ? new Date(r.reserved_start_at).getTime() : 0;
    return t >= Date.now() - UPCOMING_GRACE_MS;
  };
  const isLive = (r) =>
    ['BOOKED', 'CONFIRMED'].includes(r.reservation_status) && isFuture(r);

  const upcoming = myReservations.filter(isLive);
  const past = myReservations.filter((r) => !isLive(r));
  // "View all bookings" — collapsed by default so the booking form stays the
  // focus; expanding reveals every booking & request (upcoming + history).
  const [showAllBookings, setShowAllBookings] = useState(false);

  // Status chip for a booking (past or upcoming).
  const statusInfo = (r) => {
    // A live status whose time has already passed reads as expired, not
    // "Awaiting approval" / "Confirmed" — the slot is gone.
    if (['BOOKED', 'CONFIRMED'].includes(r.reservation_status) && !isFuture(r)) {
      return { label: 'Expired', cls: 'bg-gray-100 border-gray-200 text-gray-500' };
    }
    // On the payment clock: the table is held, not yet booked — pay and it is.
    if (r.is_held) {
      return { label: t('guest.reserve.heldLabel'), cls: 'bg-slate-100 border-slate-300 text-slate-700' };
    }
    switch (r.reservation_status) {
      case 'SEATED':
      case 'COMPLETED':
        return { label: 'Fulfilled', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' };
      case 'CANCELLED':
        return { label: 'Cancelled', cls: 'bg-red-50 border-red-200 text-red-600' };
      case 'NO_SHOW':
        return { label: 'No-show', cls: 'bg-gray-100 border-gray-200 text-gray-500' };
      case 'CONFIRMED':
        return { label: 'Confirmed', cls: 'bg-blue-50 border-blue-200 text-blue-700' };
      default:
        return { label: 'Awaiting approval', cls: 'bg-amber-50 border-amber-200 text-amber-700' };
    }
  };

  // In-page order detail (opened from a past booking's order link) — fetched
  // by order number from the public status endpoint.
  const [orderView, setOrderView] = useState(null); // {number, loading, data?, error?}
  const openOrder = async (number) => {
    setOrderView({ number, loading: true });
    try {
      const res = await fetch(
        `${API_URL}/users/orders/status/?numbers=${encodeURIComponent(number)}&details=1`);
      const d = await res.json();
      const data = d?.data?.[number];
      if (!res.ok || !data) throw new Error(d?.error || 'Order not found');
      setOrderView({ number, loading: false, data });
    } catch (e) {
      setOrderView({ number, loading: false, error: e.message || 'Could not load the order.' });
    }
  };

  // Day strip: today + the next 3 days as chips; the calendar button opens a
  // date picker for anything further out.
  const dayChips = useMemo(() => Array.from({ length: 4 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return {
      value: toDateStr(d),
      weekday: d.toLocaleDateString(undefined, { weekday: 'short' }),
      day: i === 0 ? 'Today' : String(d.getDate()),
    };
  }), []);
  const dateOnChip = dayChips.some((d) => d.value === date);
  const isToday = date === toDateStr(new Date());

  // Opening hours of the chosen branch (falls back to sensible defaults).
  const activeLocation = locations.find((l) => String(l.id) === String(locationId)) || null;
  const openMins = parseHM(activeLocation?.opening_time || DEFAULT_OPEN);
  let closeMins = parseHM(activeLocation?.closing_time || DEFAULT_CLOSE);
  if (closeMins <= openMins) closeMins += 24 * 60; // closes after midnight

  /* A BUFFET is seated for its OWN service, which is the narrower fact: a
     9-to-5 counter cannot take a table at half five however late the branch
     closes. The last seating is a quarter hour before it ends — a party sat
     down as the counter closes is served nothing. A buffet that never stated
     its hours narrows nothing. */
  const pickedBuffet = (diningType === 'BUFFET' && buffetId)
    ? buffets.find((b) => String(b.id) === String(buffetId)) || null
    : null;
  const bStart = pickedBuffet ? parseHM(pickedBuffet.start_time) : null;
  const bEnd = pickedBuffet ? parseHM(pickedBuffet.end_time) : null;
  const buffetBounded = Number.isFinite(bStart) && Number.isFinite(bEnd) && bEnd > bStart;
  const seatFrom = buffetBounded ? Math.max(openMins, bStart) : openMins;

  // A 15-min slot is bookable when it's inside those hours and not in the past.
  const lastSlot = buffetBounded
    ? Math.min(closeMins - 15, bEnd - 15)
    : closeMins - 15;
  const isSlotPast = (mins) => {
    if (!isToday) return false;
    const now = new Date();
    return mins <= now.getHours() * 60 + now.getMinutes();
  };
  const slotOpen = (mins) => mins >= seatFrom && mins <= lastSlot && !isSlotPast(mins);

  // First bookable 15-min slot today — used to prefill the typed time.
  let nextAvailable = null;
  for (let m = Math.ceil(seatFrom / 15) * 15; m <= lastSlot; m += 15) {
    if (slotOpen(m)) { nextAvailable = m; break; }
  }

  // Turn the typed hour/minute/AM-PM into minutes past midnight; null when
  // incomplete or outside opening hours (handles closing after midnight).
  const typedFilled = hourStr !== '' && minStr !== '';
  useEffect(() => {
    const h = parseInt(hourStr, 10);
    const m = parseInt(minStr, 10);
    if (!Number.isInteger(h) || h < 1 || h > 12 || !Number.isInteger(m) || m < 0 || m > 59) {
      setTimeMins(null);
      return;
    }
    let mins = ((h % 12) + (meridiem === 'PM' ? 12 : 0)) * 60 + m;
    if (mins < openMins && mins + 1440 <= closeMins) mins += 1440;
    setTimeMins(slotOpen(mins) ? mins : null);
    // Re-judged when the buffet changes too: picking a 9-to-5 counter under a
    // 5:30 pm that was fine à la carte has to take the time back off.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourStr, minStr, meridiem, date, locationId, diningType, buffetId, buffets]);

  // Prefill with the next available slot until the guest types something.
  useEffect(() => {
    if (hourStr === '' && minStr === '' && nextAvailable != null) {
      const hh = Math.floor(nextAvailable / 60) % 24;
      setHourStr(String(hh % 12 === 0 ? 12 : hh % 12));
      setMinStr(pad2(nextAvailable % 60));
      setMeridiem(hh < 12 ? 'AM' : 'PM');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, locationId]);

  // Concrete start moment (handles after-midnight slots rolling to the next day).
  const startAt = timeMins != null
    ? new Date(new Date(`${date}T00:00:00`).getTime() + timeMins * 60000)
    : null;
  const chip = (on) =>
    `shrink-0 px-4 py-2 rounded-full border text-sm font-medium transition ${
      on ? 'text-white border-transparent' : 'text-gray-700 border-gray-200 bg-white'
    }`;

  const hasSections = floors.some((f) => (f.sections || []).length > 0);

  return (
    // The global stylesheet sets body{overflow:hidden} (the staff app scrolls an
    // inner pane), so this standalone page must be its own scroll container —
    // same pattern as CustomerMenu.
    <div className="min-h-screen w-full bg-gray-100 text-gray-900 flex justify-center">
      <div className="w-full max-w-[440px] bg-white h-screen overflow-y-auto shadow-xl">
        {/* ── Sticky header: the same dark shell the customer menu wears —
            identity tile · badge row · info chips — so booking and ordering
            read as one product. ── */}
        <header className="sticky top-0 z-20 shadow-sm">
          <div className="rounded-b-3xl pb-4" style={{ background: MAROON }}>
            <div className="px-4 pt-3 flex items-center gap-3">
              <div
                className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: ACCENT }}
              >
                <Utensils className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-lg font-semibold text-white truncate leading-snug">
                  {activeLocation?.name || 'Reserve a table'}
                </div>
                <div className="mt-0.5 text-xs leading-tight flex items-center gap-2" style={{ color: '#F3B48C' }}>
                  <span className="shrink-0 inline-block rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    Reservation
                  </span>
                  {isCustomerAuthed && customerDisplayName && (
                    <span className="inline-flex items-center gap-1 min-w-0 truncate">
                      <UserRound className="h-3 w-3" />
                      {customerDisplayName}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* Info chip row: what this page does · when the branch serves */}
            <div className="px-4 mt-3">
              <div className="rounded-xl bg-white/10 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white">
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  <CalendarDays className="h-4 w-4" style={{ color: ACCENT }} />
                  Reserve a table
                </span>
                <span className="inline-flex items-center gap-1 text-white/80 text-xs">
                  <Clock className="h-3.5 w-3.5" />
                  Open {fmt12(openMins)} – {fmt12(closeMins)}
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="px-4 py-5">
        <div className="space-y-4">
          {restaurantMissing ? (
            <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="h-5 w-5 mt-0.5 text-red-500 flex-shrink-0" />
              <div>This page is meant to be opened from the restaurant's reservation link or QR code. Please ask the staff for help.</div>
            </div>
          ) : reservationsClosed && !paying && !booked ? (
            /* ── Online reservations switched off by the restaurant ───── */
            <div className="space-y-4 text-center rounded-lg border border-gray-200 p-6">
              <Clock className="h-12 w-12 mx-auto text-gray-300" />
              <div>
                <p className="text-lg font-semibold text-gray-900">Online reservations are closed</p>
                <p className="mt-1 text-sm text-gray-600">
                  The restaurant is not taking online bookings right now.
                  Please call the restaurant to reserve a table, or check back later.
                </p>
              </div>
            </div>
          ) : paying ? (
            /* ── Mock booking payment ─────────────────────────────────── */
            <div className="space-y-4 rounded-lg border border-gray-200 p-5">
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-900">Confirm with payment</p>
                <p className="mt-1 text-sm text-gray-600">
                  {new Date(paying.reserved_start_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                  {' · '}{paying.pax} {paying.pax === 1 ? 'guest' : 'guests'}
                </p>
                <div className="mt-3 inline-block rounded-xl px-5 py-3" style={{ background: TINT }}>
                  {/* A deposit and the whole ticket are not the same thing to
                      a guest reading this, so they are not called the same. */}
                  <div className="text-xs text-gray-500">
                    {bookingPolicy?.amount_mode === 'FULL' ? 'Buffet total' : 'Booking amount'}
                  </div>
                  <div className="text-2xl font-bold" style={{ color: ORANGE }}>
                    Rs {Number(paying.payment_amount || 0).toFixed(2)}
                  </div>
                  {bookingPolicy?.amount_mode === 'FULL' && (
                    <div className="mt-0.5 text-[11px] text-gray-500">
                      The whole buffet — nothing left to pay at the table.
                    </div>
                  )}
                </div>
              </div>

              {/* The seat hold. Shown as a live countdown while it runs, and
                  as what actually happened once it has: the booking is already
                  cancelled server-side and the covers are back on the counter,
                  so “pay” is no longer an offer that can be honoured. */}
              {holdLeft != null && (
                <div className={`rounded-lg border px-4 py-3 text-sm ${
                  holdExpired
                    ? 'border-red-200 bg-red-50 text-red-700'
                    : 'border-amber-200 bg-amber-50 text-amber-800'
                }`}>
                  {holdExpired ? (
                    <span className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>Your seats were released — the payment window ran out. Please book again.</span>
                    </span>
                  ) : (
                    <span className="flex items-start gap-2">
                      <Clock className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        Your seats are held for <strong>{mmss(holdLeft)}</strong> — paying confirms
                        them straight away, with nothing to wait for the restaurant to approve.
                      </span>
                    </span>
                  )}
                </div>
              )}

              {error && (
                <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="h-5 w-5 mt-0.5 text-red-500 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Mock card form — no real gateway behind it. */}
              <div className="space-y-3">
                <input className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none text-gray-900 placeholder:text-gray-400"
                  inputMode="numeric" placeholder="Card number" value={cardNo} disabled={payBusy}
                  onChange={(e) => setCardNo(e.target.value.replace(/[^\d ]/g, '').slice(0, 19))} />
                <div className="grid grid-cols-2 gap-3">
                  <input className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none text-gray-900 placeholder:text-gray-400"
                    placeholder="MM/YY" value={cardExp} disabled={payBusy}
                    onChange={(e) => setCardExp(e.target.value.replace(/[^\d/]/g, '').slice(0, 5))} />
                  <input className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none text-gray-900 placeholder:text-gray-400"
                    inputMode="numeric" placeholder="CVV" value={cardCvv} disabled={payBusy}
                    onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                </div>
              </div>

              {policy && (
                <p className="text-xs text-gray-500">
                  {policy.refund_allowed
                    ? `Cancel at least ${policy.refund_cutoff_minutes} minutes before your booking and this is refunded in full — ${refundHow(policy.refund_method)}.`
                    : 'The booking amount is not refundable on cancellation.'}
                </p>
              )}

              <button type="button" onClick={payNow} disabled={payBusy || holdExpired}
                className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-lg disabled:opacity-60 text-white font-medium tracking-wide shadow-sm transition"
                style={{ background: ORANGE }}>
                {payBusy ? 'Processing…'
                  : holdExpired ? 'The hold has run out'
                    : `Pay Rs ${Number(paying.payment_amount || 0).toFixed(2)}`}
                {!payBusy && !holdExpired && <ArrowRight className="h-4 w-4" />}
              </button>
              {/* Once the clock has run out there is nothing left to cancel —
                  the sweep already did it — so the way out is back to the form. */}
              {holdExpired ? (
                <button type="button"
                  onClick={() => { setPaying(null); setError(''); loadMyReservations(); }}
                  className="w-full text-center text-xs text-gray-400 hover:text-gray-600">
                  Back to booking
                </button>
              ) : (
                <button type="button" onClick={abandonPayment} disabled={payBusy}
                  className="w-full text-center text-xs text-gray-400 hover:text-gray-600">
                  Cancel this booking instead
                </button>
              )}
              <p className="text-center text-[10px] text-gray-300">Demo payment — no card is charged.</p>
            </div>
          ) : booked ? (
            <div className="space-y-5 text-center rounded-lg border border-gray-200 p-5">
              {booked.reservation_status === 'CONFIRMED'
                ? <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
                : <Clock className="h-12 w-12 mx-auto" style={{ color: ACCENT }} />}
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {booked.reservation_status === 'CONFIRMED'
                    ? 'Table reserved!'
                    : 'Your reservation request is sent'}
                </p>
                {booked.reservation_status !== 'CONFIRMED' && (
                  <p className="mt-1 text-sm text-gray-600">
                    The restaurant is reviewing it — your confirmation will be sent shortly.
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-600">
                  {new Date(booked.reserved_start_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                  {' · '}{booked.pax} {booked.pax === 1 ? 'guest' : 'guests'}
                  {/* Table only once staff has approved — before that the
                      assignment is provisional and may change. */}
                  {booked.reservation_status === 'CONFIRMED' && booked.table_code
                    ? ` · Table ${booked.table_code}` : ''}
                </p>
                {booked.dining_type === 'BUFFET' && booked.buffet_name && (
                  <p className="mt-1 text-sm font-medium" style={{ color: ORANGE }}>
                    Buffet · {booked.buffet_name} (all you can eat)
                  </p>
                )}
                {booked.payment_status === 'PAID' && (
                  <p className="mt-1 text-sm text-emerald-700 font-medium">
                    Booking amount paid · Rs {Number(booked.payment_amount || 0).toFixed(2)}
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-400">
                  Reservation code: <span className="font-mono">{booked.reservation_code}</span>
                </p>
                {policy && (
                  <p className="mt-2 text-xs text-gray-400">
                    Your table is held for {policy.hold_table_minutes} minutes past your booking
                    time. Running late? Call the restaurant — they can hold it longer for you.
                  </p>
                )}
              </div>

              {canPreorder(booked) && booked.reservation_status !== 'CONFIRMED' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  You can pre-order your meal once the restaurant confirms this reservation.
                </div>
              )}
              {canPreorder(booked) && booked.reservation_status === 'CONFIRMED' && (
                booked.has_preorder ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                    Pre-order saved — the kitchen starts it the moment you check in.
                  </div>
                ) : (
                  <button type="button" disabled={preorderBusy}
                    onClick={() => startPreorder(booked)}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-white font-medium tracking-wide shadow-sm transition disabled:opacity-60"
                    style={{ background: ORANGE }}>
                    <UtensilsCrossed className="h-4 w-4" />
                    {preorderBusy ? 'Opening the menu…' : 'Pre-order your meal'}
                  </button>
                )
              )}

              <button
                type="button"
                onClick={() => setBooked(null)}
                className="w-full py-3 px-4 rounded-lg border font-medium transition"
                style={{ borderColor: ORANGE, color: ORANGE }}
              >
                Make another reservation
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="h-5 w-5 mt-0.5 text-red-500 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Your information — signed in, or a sign-in prompt for guests */}
              <Section title="Your information">
                {isCustomerAuthed ? (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <UserRound className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="truncate">
                      {customerDisplayName || 'Signed in'}
                      {profile.phone ? ` · ${profile.phone}` : ''}
                    </span>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-3">
                      Sign in to finish your booking — your details are filled in for you.
                    </p>
                    <button type="button" onClick={promptLogin}
                      className="w-full py-3 rounded-md border-2 border-gray-900 text-sm font-semibold text-gray-900">
                      Log In
                    </button>
                    <p className="mt-3 text-xs text-gray-400 text-center">
                      You can fill the rest in first — nothing is lost when you sign in.
                    </p>
                  </>
                )}
              </Section>

              {locations.length > 1 && (
                <Section title="Branch">
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <select
                      value={locationId}
                      onChange={(e) => setLocationId(e.target.value)}
                      disabled={submitting}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#8C3A23]/30 focus:border-[#8C3A23] outline-none transition text-gray-900 placeholder:text-gray-400"
                    >
                      <option value="">Choose a branch…</option>
                      {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                </Section>
              )}

              {/* Date · time · party size — one card, top to bottom */}
              <Section title="Date & time">
                <label className="block text-xs text-gray-500 mb-2">Date</label>
                <div className="grid grid-cols-5 gap-2">
                  {dayChips.map((d) => {
                    const on = d.value === date;
                    return (
                      <button key={d.value} type="button" disabled={submitting}
                        onClick={() => { setDate(d.value); setShowDatePicker(false); }}
                        className={`rounded-xl border py-2 text-center transition ${
                          on ? 'text-white border-transparent shadow-sm' : 'text-gray-700 border-gray-200 bg-white'
                        }`}
                        style={on ? { background: ORANGE } : {}}>
                        <div className={`text-[11px] ${on ? 'text-white/80' : 'text-gray-400'}`}>{d.weekday}</div>
                        <div className="text-sm font-semibold leading-tight">{d.day}</div>
                      </button>
                    );
                  })}
                  <button type="button" disabled={submitting}
                    onClick={() => setShowDatePicker((v) => !v)}
                    className={`rounded-xl border flex items-center justify-center transition ${
                      !dateOnChip ? 'text-white border-transparent shadow-sm' : 'text-gray-700 border-gray-200 bg-white'
                    }`}
                    style={!dateOnChip ? { background: ORANGE } : {}}
                    title="Pick another date">
                    <CalendarDays className="h-5 w-5" />
                  </button>
                </div>
                {(showDatePicker || !dateOnChip) && (
                  <input type="date" value={date} min={toDateStr(new Date())} disabled={submitting}
                    onChange={(e) => e.target.value && setDate(e.target.value)}
                    className="mt-2 w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8C3A23]/30 focus:border-[#8C3A23] outline-none transition text-gray-900 placeholder:text-gray-400" />
                )}

                {/* Time — the guest types hour & minute directly */}
                <label className="block text-xs text-gray-500 mt-4">Time</label>
                {/* A buffet booking is seated for the buffet's hours, so that is what the hint says. */}
                <p className="text-xs text-gray-400 mb-2">
                  {buffetBounded
                    ? `${pickedBuffet.name || 'The buffet'} served ${fmt12(bStart)} – ${fmt12(bEnd)}`
                    : `Open ${fmt12(openMins)} – ${fmt12(closeMins)}`}
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="text" inputMode="numeric" maxLength={2} disabled={submitting}
                    value={hourStr}
                    onChange={(e) => setHourStr(e.target.value.replace(/\D/g, ''))}
                    placeholder="7" aria-label="Hour"
                    className="w-24 py-3 text-center text-lg font-semibold border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#8C3A23]/30 focus:border-[#8C3A23] outline-none transition"
                  />
                  <span className="text-lg font-bold text-gray-400">:</span>
                  <input
                    type="text" inputMode="numeric" maxLength={2} disabled={submitting}
                    value={minStr}
                    onChange={(e) => setMinStr(e.target.value.replace(/\D/g, ''))}
                    onBlur={() => setMinStr((v) => (v === '' ? v : pad2(parseInt(v, 10) || 0)))}
                    placeholder="30" aria-label="Minute"
                    className="w-24 py-3 text-center text-lg font-semibold border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#8C3A23]/30 focus:border-[#8C3A23] outline-none transition"
                  />
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    {['AM', 'PM'].map((mer) => (
                      <button key={mer} type="button" disabled={submitting}
                        onClick={() => setMeridiem(mer)}
                        className={`px-3 py-3 text-sm font-semibold transition ${
                          meridiem === mer ? 'text-white' : 'text-gray-500 bg-white'
                        }`}
                        style={meridiem === mer ? { background: ORANGE } : {}}>
                        {mer}
                      </button>
                    ))}
                  </div>
                </div>
                {typedFilled && timeMins == null && (
                  <p className="mt-2 text-xs text-red-500">
                    {nextAvailable == null
                      ? (buffetBounded
                        ? `${pickedBuffet.name || 'That buffet'} is served ${fmt12(bStart)} to ${fmt12(bEnd)} — nothing left to book on this date.`
                        : 'No slots left for this day — try another date.')
                      : buffetBounded
                        ? `That time isn't available — ${pickedBuffet.name || 'the buffet'} is served ${fmt12(bStart)} to ${fmt12(bEnd)}${isToday ? ', and past times today can’t be booked' : ''}.`
                        : `That time isn't available — we're open ${fmt12(openMins)} to ${fmt12(closeMins)}${isToday ? ', and past times today can’t be booked' : ''}.`}
                  </p>
                )}

                <label className="block text-xs text-gray-500 mt-4">Guests</label>
                <p className="text-xs text-gray-400 mb-2">
                  {pax >= maxParty
                    ? `Up to ${maxParty} guests can be booked online — for a larger party, please call us`
                    : pax <= 4 ? 'Fits a standard 4-seater' : "We'll arrange a larger table"}
                </p>
                <div className="flex items-center gap-4">
                  <button type="button" onClick={() => setPax((n) => Math.max(1, Number(n) - 1))}
                    className="h-11 w-11 rounded-xl border flex items-center justify-center text-xl"
                    style={{ borderColor: ORANGE, color: ORANGE }}>−</button>
                  <span className="inline-flex items-center gap-2 text-lg font-semibold text-gray-900 w-16 justify-center">
                    <Users className="h-4 w-4 text-gray-400" />{pax}
                  </span>
                  <button type="button" onClick={() => setPax((n) => Math.min(maxParty, Number(n) + 1))}
                    disabled={pax >= maxParty}
                    className="h-11 w-11 rounded-xl border flex items-center justify-center text-xl"
                    style={{ borderColor: ORANGE, color: ORANGE }}>+</button>
                </div>
              </Section>

              {/* Dining style — regular à-la-carte menu or an open buffet */}
              {locationId && (
                <Section title="Dining style">
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button"
                      onClick={() => { setDiningType('ALACARTE'); setBuffetId(''); }}
                      className={`flex items-center justify-center gap-2 py-3 px-3 rounded-lg border text-sm font-medium transition ${
                        diningType === 'ALACARTE' ? 'text-white border-transparent' : 'text-gray-700 border-gray-200 bg-white'
                      }`}
                      style={diningType === 'ALACARTE' ? { background: ORANGE } : {}}>
                      <Utensils className="h-4 w-4" /> À la carte
                    </button>
                    <button type="button" disabled={!buffetsLoading && buffets.length === 0}
                      onClick={() => {
                        setDiningType('BUFFET');
                        if (buffets.length === 1) setBuffetId(String(buffets[0].id));
                      }}
                      className={`flex flex-col items-center justify-center gap-0.5 py-2 px-3 rounded-lg border text-sm font-medium transition disabled:opacity-50 ${
                        diningType === 'BUFFET' ? 'text-white border-transparent' : 'text-gray-700 border-gray-200 bg-white'
                      }`}
                      style={diningType === 'BUFFET' ? { background: ORANGE } : {}}>
                      <span className="inline-flex items-center gap-2">
                        <UtensilsCrossed className="h-4 w-4" /> Buffet
                      </span>
                      <span className={`text-[10px] font-normal ${diningType === 'BUFFET' ? 'text-white/80' : 'text-gray-400'}`}>
                        all you can eat
                      </span>
                    </button>
                  </div>

                  {diningType === 'ALACARTE' && (
                    <p className="mt-2 text-xs text-gray-500">
                      Order dishes from the menu at your table.
                    </p>
                  )}
                  {!buffetsLoading && buffets.length === 0 && (
                    <p className="mt-2 text-xs text-gray-400">
                      No buffet is offered at this branch — à la carte only.
                    </p>
                  )}

                  {diningType === 'BUFFET' && (
                    buffetsLoading ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading buffets…
                      </div>
                    ) : (
                      <div className="mt-3 space-y-2">
                        {buffets.map((b) => {
                          const on = String(b.id) === String(buffetId);
                          return (
                            <button key={b.id} type="button" disabled={b.seats_left === 0}
                              onClick={() => setBuffetId(String(b.id))}
                              className={`w-full text-left rounded-lg border p-3 transition ${
                                on ? 'shadow-sm' : 'border-gray-200 bg-white'
                              } ${b.seats_left === 0 ? 'opacity-60 cursor-not-allowed' : ''}`}
                              style={on ? { borderColor: ORANGE, background: TINT } : {}}>
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium text-gray-900 text-sm">{b.name}</span>
                                <span className="text-sm font-semibold" style={{ color: ORANGE }}>
                                  Rs {Number(b.adult_price).toFixed(0)}<span className="text-[10px] font-normal text-gray-500"> /adult</span>
                                </span>
                              </div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                                <span>{b.buffet_type_label}</span>
                                {(b.start_time || b.end_time) && <span>· {b.start_time || '…'}–{b.end_time || '…'}</span>}
                                {b.child_price != null && (
                                  <span>· Rs {Number(b.child_price).toFixed(0)} /child up to {b.child_age_limit} yrs</span>
                                )}
                                {b.price_label && <span style={{ color: ORANGE }}>· {b.price_label}</span>}
                                {/* Covers nobody has claimed on this date. Absent
                                    means the buffet has never said how many it
                                    seats — wide open, not full — so nothing is
                                    printed rather than a scary zero. */}
                                {b.seats_left != null && (
                                  <span className={
                                    b.seats_left === 0 ? 'font-medium text-red-600'
                                      : b.seats_left <= 5 ? 'font-medium text-amber-600' : ''
                                  }>
                                    · {b.seats_left === 0
                                      ? 'Fully booked'
                                      : `${b.seats_left} ${b.seats_left === 1 ? 'seat' : 'seats'} left`}
                                  </span>
                                )}
                              </div>
                              {(b.price_tiers || []).length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {b.price_tiers.map((t) => (
                                    <span key={t.label} className="px-1.5 py-0.5 rounded-full text-[10px] border border-gray-200 bg-white text-gray-600">
                                      {t.label} Rs {Number(t.price).toFixed(0)}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {(b.menu_name || (b.menus || []).length > 0) && (
                                <p className="mt-1 text-[11px] text-gray-400 line-clamp-1">
                                  {[b.menu_name, ...(b.menus || []).map((m) => m.name)].filter(Boolean).join(' · ')}
                                </p>
                              )}
                            </button>
                          );
                        })}
                        {/* Which sitting. Only shown for a buffet that is split
                            into them; every other buffet books the window as it
                            always did, and the time box above is the answer. */}
                        {buffetSittings.length > 0 && (
                          <div className="rounded-lg border border-gray-200 bg-white p-3">
                            <div className="text-sm font-medium text-gray-900">{t('buffet.sit.pick')}</div>
                            <p className="mt-0.5 text-xs text-gray-500">{t('buffet.sit.pickHint')}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {buffetSittings.map((s) => {
                                const mins = parseHM(s.start);
                                const on = timeMins === mins;
                                const full = s.seats_left === 0;
                                return (
                                  <button key={s.start} type="button" disabled={full}
                                    onClick={() => pickSitting(s.start)}
                                    className={`rounded-lg border px-2.5 py-1.5 text-left transition ${
                                      on ? 'shadow-sm' : 'border-gray-200 bg-white'
                                    } ${full ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    style={on ? { borderColor: ORANGE, background: TINT } : {}}>
                                    <div className="text-xs font-medium text-gray-900 whitespace-nowrap">
                                      {fmt12(mins)} – {fmt12(parseHM(s.end))}
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-gray-500">
                                      {s.adult != null && (
                                        <span style={{ color: ORANGE }} className="font-semibold">
                                          Rs {Number(s.adult).toFixed(0)}
                                        </span>
                                      )}
                                      {/* Absent = this sitting was never given a
                                          seat count and the day is uncapped —
                                          wide open, which is not "0 left". */}
                                      {s.seats_left != null && (
                                        <span className={full ? 'font-medium text-red-600'
                                          : s.seats_left <= 5 ? 'font-medium text-amber-600' : ''}>
                                          {full ? t('buffet.sit.full')
                                            : s.seats_left === 1 ? t('buffet.sit.seatLeft')
                                              : t('buffet.sit.seatsLeft', { n: s.seats_left })}
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-500">
                          Buffet is charged per person at the price shown for your visit time.
                        </p>
                      </div>
                    )
                  )}
                </Section>
              )}

              {/* Seating preference — best available (default) or a section */}
              {locationId && (
                <Section title="Seating preference">
                  {tablesLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading seating options…
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <button type="button"
                          onClick={() => { setSeatMode('best'); setFloorId(''); setSectionId(''); }}
                          className={`flex items-center justify-center gap-2 py-3 px-3 rounded-lg border text-sm font-medium transition ${
                            seatMode === 'best' ? 'text-white border-transparent' : 'text-gray-700 border-gray-200 bg-white'
                          }`}
                          style={seatMode === 'best' ? { background: ORANGE } : {}}>
                          <Sparkles className="h-4 w-4" /> Best available
                        </button>
                        <button type="button" disabled={!hasSections}
                          onClick={() => {
                            setSeatMode('section');
                            const f0 = floors.find((f) => (f.sections || []).length) || floors[0];
                            if (f0) {
                              setFloorId(String(f0.id));
                              const s0 = (f0.sections || [])[0];
                              setSectionId(s0 ? String(s0.id) : '');
                            }
                          }}
                          className={`flex items-center justify-center gap-2 py-3 px-3 rounded-lg border text-sm font-medium transition disabled:opacity-50 ${
                            seatMode === 'section' ? 'text-white border-transparent' : 'text-gray-700 border-gray-200 bg-white'
                          }`}
                          style={seatMode === 'section' ? { background: ORANGE } : {}}>
                          <LayoutGrid className="h-4 w-4" /> Pick a section
                        </button>
                      </div>

                      {seatMode === 'best' && (
                        <p className="text-xs text-gray-500">
                          We'll seat you at the best table for your party size.
                        </p>
                      )}

                      {seatMode === 'section' && (
                        <>
                          {/* Floor chips */}
                          {floors.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                              {floors.map((f) => {
                                const on = String(f.id) === String(floorId);
                                return (
                                  <button key={f.id} type="button"
                                    onClick={() => {
                                      setFloorId(String(f.id));
                                      const s0 = (f.sections || [])[0];
                                      setSectionId(s0 ? String(s0.id) : '');
                                    }}
                                    className={chip(on)} style={on ? { background: ORANGE } : {}}>
                                    {f.name}
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {/* Section chips */}
                          <div className="flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                            {(activeFloor?.sections || []).map((s) => {
                              const on = String(s.id) === String(sectionId);
                              return (
                                <button key={s.id} type="button" onClick={() => setSectionId(String(s.id))}
                                  className={chip(on)} style={on ? { background: ORANGE } : {}}>
                                  {s.name}
                                </button>
                              );
                            })}
                          </div>

                          <p className="text-xs text-gray-500">
                            {activeSection
                              ? <>We'll pick the best table in <span className="font-medium text-gray-700">{activeSection.name}</span> for your party.</>
                              : 'Choose a section and we\'ll pick the best table in it.'}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </Section>
              )}

              {/* Live availability — auto-confirm restaurants only. */}
              {availability != null && (
                availability === 'checking' ? (
                  <p className="text-xs text-gray-400">Checking availability…</p>
                ) : availability.available ? (
                  <p className="text-sm rounded-lg px-3 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700">
                    ✓ A table for {pax} is available
                    {seatMode === 'section' && activeSection ? <> in <span className="font-medium">{activeSection.name}</span></> : null} at that time
                    — it's confirmed the moment you book.
                  </p>
                ) : (
                  <p className="text-sm rounded-lg px-3 py-2 bg-red-50 border border-red-200 text-red-700">
                    No table for {pax} is free
                    {seatMode === 'section' && activeSection ? <> in <span className="font-medium">{activeSection.name}</span></> : null} at that time —
                    try another time{seatMode === 'section' ? ', section' : ''} or party size.
                  </p>
                )
              )}

              {/* Optional preferences — collapsed rows, opened only if needed */}
              <p className="text-sm text-gray-600 px-1">
                If you'd like, tell us more about your visit so we can look after you better.
              </p>
              <div className="rounded-lg border border-gray-200 bg-white px-4">
                <Accordion title="Is this a special occasion?"
                  open={openPanel === 'occasion'} onToggle={() => togglePanel('occasion')}>
                  <div className="space-y-2">
                    {OCCASION_OPTS.map((opt) => (
                      <label key={opt} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                        <input type="checkbox" checked={requestOpts.includes(opt)} disabled={submitting}
                          onChange={() => toggleRequestOpt(opt)}
                          className="h-4 w-4 rounded border-gray-300" style={{ accentColor: ORANGE }} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </Accordion>

                <Accordion title="Any dietary restrictions or allergies?"
                  open={openPanel === 'dietary'} onToggle={() => togglePanel('dietary')}>
                  <textarea rows={2} placeholder="e.g. nut allergy, vegetarian, no pork"
                    value={dietary} onChange={(e) => setDietary(e.target.value)} disabled={submitting}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm outline-none focus:border-gray-500 resize-none" />
                </Accordion>

                <Accordion title="Any seating or access needs?"
                  open={openPanel === 'seating'} onToggle={() => togglePanel('seating')}>
                  <div className="space-y-2">
                    {ACCESS_OPTS.map((opt) => (
                      <label key={opt} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                        <input type="checkbox" checked={requestOpts.includes(opt)} disabled={submitting}
                          onChange={() => toggleRequestOpt(opt)}
                          className="h-4 w-4 rounded border-gray-300" style={{ accentColor: ORANGE }} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </Accordion>

                <Accordion title="Anything else we should know?"
                  open={openPanel === 'other'} onToggle={() => togglePanel('other')}>
                  <textarea rows={2} placeholder="Surprise plans, seating notes…"
                    value={notes} onChange={(e) => setNotes(e.target.value)} disabled={submitting}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm outline-none focus:border-gray-500 resize-none" />
                </Accordion>
              </div>

              {/* Consent — the cancellation policy must be ticked to submit */}
              <div className="space-y-3 px-1">
                <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer select-none">
                  <input type="checkbox" checked={policyAccepted} disabled={submitting}
                    onChange={(e) => setPolicyAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300" style={{ accentColor: ORANGE }} />
                  <span>
                    Cancellation policy*
                    <span className="block text-xs text-gray-500">
                      {policy
                        ? `Your table is held for ${policy.hold_table_minutes} minutes past your booking time.${
                          policy.payment_required && Number(policy.booking_amount) > 0
                            ? policy.refund_allowed
                              ? ` The booking amount is refunded ${refundHow(policy.refund_method)} if you cancel at least ${policy.refund_cutoff_minutes} minutes before.`
                              : ' The booking amount is not refundable on cancellation.'
                            : ''}`
                        : 'Please cancel in advance if your plans change.'}
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer select-none">
                  <input type="checkbox" checked={smsOptIn} disabled={submitting}
                    onChange={(e) => setSmsOptIn(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300" style={{ accentColor: ORANGE }} />
                  <span>Send me reminders and confirmations for this reservation.</span>
                </label>
              </div>

              <button type="submit" disabled={submitting}
                className="w-full py-3.5 px-5 rounded-md disabled:opacity-60 text-white text-base font-semibold transition"
                style={{ background: ORANGE }}>
                {submitting
                  ? 'Booking…'
                  : timeMins == null ? 'Pick a time to continue'
                    : browseOnly ? 'Submit — sign in to confirm'
                      : `Submit · ${isToday ? 'Today' : new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${fmt12(timeMins)}`}
              </button>

              {/* Buffet paid in full: the number is the party's own ticket,
                  worked out the way the server will — adults at the day's adult
                  price — so the figure quoted here is the figure charged. */}
              {bookingPolicy?.payment_required && bookingPolicy.amount_mode === 'FULL' && pickedBuffet && (
                <p className="text-xs text-gray-500 -mt-2 px-1">
                  Rs {(Number(pickedBuffet.adult_price || 0) * (Number(pax) || 1)).toFixed(0)}
                  {' '}for {pax} {Number(pax) === 1 ? 'guest' : 'guests'} — the whole buffet, paid now.
                  Your seats are held while you pay
                  {policy?.buffet_seat_hold_minutes ? ` (${policy.buffet_seat_hold_minutes} minutes)` : ''}
                  {' '}and paying confirms them — the restaurant approves nothing.
                </p>
              )}
              {/* A table on the website clock: said before the amount, because
                  it changes what "book" means — the table is theirs the moment
                  they pay, and gone if they don't. */}
              {bookingPolicy?.payment_required && bookingPolicy.amount_mode !== 'FULL'
                && Number(bookingPolicy.booking_amount) > 0 && !pickedBuffet
                && Number(policy?.table_hold_minutes) > 0 && (
                <p className="text-xs text-slate-700 -mt-2 px-1 font-medium">
                  {t('guest.reserve.tableHoldHint', { n: policy.table_hold_minutes })}
                </p>
              )}
              {bookingPolicy?.payment_required && bookingPolicy.amount_mode !== 'FULL'
                && Number(bookingPolicy.booking_amount) > 0 && (
                <p className="text-xs text-gray-500 -mt-2 px-1">
                  A booking amount of Rs {Number(bookingPolicy.booking_amount).toFixed(0)}
                  {bookingPolicy.amount_per === 'GUEST' ? ' per guest' : ''} confirms your table
                  {bookingPolicy.refund_allowed
                    ? ` — fully refunded if you cancel at least ${bookingPolicy.refund_cutoff_minutes} minutes before, ${refundHow(policy.refund_method)}.`
                    : ' (non-refundable on cancellation).'}
                </p>
              )}
            </form>
          )}
        </div>

        {/* View all bookings — one tap reveals every booking & request */}
        {myReservations.length > 0 && !restaurantMissing && (
          <div className="mt-6 text-center">
            <button type="button" onClick={() => setShowAllBookings((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold bg-white"
              style={{ borderColor: ORANGE, color: ORANGE }}>
              <CalendarDays className="h-4 w-4" />
              {showAllBookings
                ? 'Hide my bookings'
                : `View all my bookings (${myReservations.length})`}
            </button>
          </div>
        )}

        {showAllBookings && upcoming.length > 0 && !restaurantMissing && (
          <div className="mt-6 bg-white rounded-2xl shadow border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Your upcoming reservations</h2>
            <ul className="space-y-2">
              {upcoming.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium text-gray-800">
                      {new Date(r.reserved_start_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                    <div className="text-xs text-gray-500">
                      {r.pax} {r.pax === 1 ? 'guest' : 'guests'}
                      {r.reservation_status === 'CONFIRMED' && r.table_code ? ` · Table ${r.table_code}` : ''}
                      {r.dining_type === 'BUFFET' && r.buffet_name ? ` · Buffet: ${r.buffet_name}` : ''}
                      {r.payment_status === 'PAID' ? ` · Paid Rs ${Number(r.payment_amount || 0).toFixed(0)}` : ''}
                      {r.has_preorder ? ' · Pre-ordered' : ''}
                      {' · '}<span className="font-mono">{r.reservation_code}</span>
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded-full border text-[10px] font-semibold mr-1.5 align-middle ${statusInfo(r).cls}`}>
                        {statusInfo(r).label}
                      </span>
                      {r.created_at && <>Booked on {new Date(r.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</>}
                    </div>
                    {canPreorder(r) && !r.has_preorder && r.reservation_status === 'CONFIRMED' && (
                      <button type="button" disabled={preorderBusy}
                        onClick={() => startPreorder(r)}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium hover:underline disabled:opacity-60"
                        style={{ color: ORANGE }}>
                        <UtensilsCrossed className="h-3.5 w-3.5" /> Pre-order your meal
                      </button>
                    )}
                  </div>
                  <button type="button" onClick={() => cancelReservation(r.id)} title="Cancel reservation"
                    className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700">
                    <XCircle className="h-4 w-4" /> Cancel
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Booking history — cancelled / fulfilled / no-show, with order link */}
        {showAllBookings && past.length > 0 && !restaurantMissing && (
          <div className="mt-6 bg-white rounded-2xl shadow border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Past bookings</h2>
            <ul className="space-y-2">
              {past.map((r) => (
                <li key={r.id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium text-gray-800">
                      {new Date(r.reserved_start_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusInfo(r).cls}`}>
                      {statusInfo(r).label}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {r.pax} {r.pax === 1 ? 'guest' : 'guests'}
                    {r.table_code ? ` · Table ${r.table_code}` : ''}
                    {r.dining_type === 'BUFFET' && r.buffet_name ? ` · Buffet: ${r.buffet_name}` : ''}
                    {r.payment_status === 'PAID' ? ` · Paid Rs ${Number(r.payment_amount || 0).toFixed(0)}` : ''}
                    {r.payment_status === 'REFUNDED' ? ` · Rs ${Number(r.payment_amount || 0).toFixed(0)} refunded` : ''}
                    {r.payment_status === 'FORFEITED' ? ` · Rs ${Number(r.payment_amount || 0).toFixed(0)} forfeited` : ''}
                    {r.payment_status === 'APPLIED'
                      ? ` · Rs ${Number(r.payment_amount || 0).toFixed(0)} adjusted in your bill${r.payment_applied_order_number ? ` (${r.payment_applied_order_number})` : ''}`
                      : ''}
                    {' · '}<span className="font-mono">{r.reservation_code}</span>
                  </div>
                  <div className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
                    {r.created_at && <>Booked on {new Date(r.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</>}
                    {r.order_number && (
                      <button type="button" onClick={() => openOrder(r.order_number)}
                        className="font-mono font-medium underline underline-offset-2 hover:opacity-80"
                        style={{ color: ORANGE }}>
                        Order {r.order_number}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Order detail sheet (opened from a past booking's order link) */}
        {orderView && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
            onClick={() => setOrderView(null)}>
            <div className="w-full max-w-[400px] bg-white rounded-2xl shadow-xl p-5 max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">
                  Order <span className="font-mono">{orderView.number}</span>
                </h3>
                <button type="button" onClick={() => setOrderView(null)} aria-label="Close"
                  className="text-gray-400 hover:text-gray-600">
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
              {orderView.loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading order…
                </div>
              ) : orderView.error ? (
                <p className="text-sm text-red-600 py-4">{orderView.error}</p>
              ) : (
                <>
                  <p className="text-xs text-gray-500 mb-3">
                    {orderView.data.placed_at && <>Placed {new Date(orderView.data.placed_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })} · </>}
                    Status: <span className="font-medium text-gray-700">{orderView.data.status}</span>
                  </p>
                  <ul className="divide-y divide-gray-100">
                    {(orderView.data.items || []).map((it, i) => (
                      <li key={i} className="py-2 flex items-start justify-between gap-3 text-sm">
                        <div>
                          <div className="text-gray-800">{it.name}</div>
                          {(it.addons || []).length > 0 && (
                            <div className="text-[11px] text-gray-400">+ {(it.addons || []).map((a) => a.name || a).join(', ')}</div>
                          )}
                        </div>
                        <div className="text-right shrink-0 text-gray-600 tabular-nums">
                          ×{it.qty || 1}
                          {it.unitPrice != null && <div className="text-[11px] text-gray-400">Rs {Number(it.unitPrice).toFixed(0)}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-sm">
                    <span className="text-gray-500">Total</span>
                    <span className="font-semibold text-gray-900">Rs {Number(orderView.data.total || 0).toFixed(2)}</span>
                  </div>
                  {orderView.data.note && (
                    <p className="mt-2 text-xs text-gray-400">“{orderView.data.note}”</p>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-gray-400">Powered by RestoBoxx · Secure customer entry</p>
        </div>
      </div>
    </div>
  );
}
